import React, { useEffect, useState, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { MapContainer, TileLayer, Marker, Popup, useMap, CircleMarker } from 'react-leaflet';
import L from 'leaflet';
import {
  Box,
  Typography,
  Avatar,
  Chip,
  Button,
  CircularProgress,
  Paper,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import {
  Pets as PetsIcon,
  LocationOn,
  FavoriteBorder,
  LightMode,
  DarkMode,
} from '@mui/icons-material';
import { catService, getImageUrl, hasValidLocation } from '../../services/api';
import 'leaflet/dist/leaflet.css';

// Fix default marker icon
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// Custom cat marker icon
const catIcon = L.divIcon({
  className: '',
  html: `
    <div style="
      background: linear-gradient(135deg, #7c3aed 0%, #a855f7 100%);
      width: 36px;
      height: 36px;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      box-shadow: 0 4px 12px rgba(124, 58, 237, 0.4);
      border: 3px solid rgba(255,255,255,0.9);
    ">
      <svg style="width:20px;height:20px;fill:white" viewBox="0 0 24 24">
        <path d="M12,8L10.67,8.09C9.81,7.07 7.4,4.5 5,4.5C5,4.5 3.03,7.46 4.96,11.41C4.41,12.24 4.07,12.67 4,13.66L2.07,13.95L2.28,14.93L4.04,14.67L4.18,15.38L2.61,16.32L3.08,17.21L4.53,16.32C5.68,18.76 8.59,20 12,20C15.41,20 18.32,18.76 19.47,16.32L20.92,17.21L21.39,16.32L19.82,15.38L19.96,14.67L21.72,14.93L21.93,13.95L20,13.66C19.93,12.67 19.59,12.24 19.04,11.41C20.97,7.46 19,4.5 19,4.5C16.6,4.5 14.19,7.07 13.33,8.09L12,8M9,11A1,1 0 0,1 10,12A1,1 0 0,1 9,13A1,1 0 0,1 8,12A1,1 0 0,1 9,11M15,11A1,1 0 0,1 16,12A1,1 0 0,1 15,13A1,1 0 0,1 14,12A1,1 0 0,1 15,11M11,14H13V16H11V14Z" />
      </svg>
    </div>
  `,
  iconSize: [36, 36],
  iconAnchor: [18, 18],
  popupAnchor: [0, -20],
});

interface Cat {
  _id: string;
  name: string;
  breed: string;
  imageUrl: string;
  location: { latitude: number; longitude: number };
  likes_pats: boolean;
  uploadedBy: { username: string };
}

// Component to recenter map
const RecenterMap: React.FC<{ center: [number, number] | null }> = ({ center }) => {
  const map = useMap();
  useEffect(() => {
    if (center) {
      map.flyTo(center, 15, { duration: 1.5 });
    }
  }, [center, map]);
  return null;
};

const MapView: React.FC = () => {
  const [cats, setCats] = useState<Cat[]>([]);
  const [mapAppearance, setMapAppearance] = useState<'light' | 'dark'>('dark');
  const [catsLoading, setCatsLoading] = useState(true);
  const [locationLoading, setLocationLoading] = useState(true);
  const [userLocation, setUserLocation] = useState<[number, number] | null>(null);
  const [targetCenter, setTargetCenter] = useState<[number, number] | null>(null);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const mapRef = useRef<L.Map | null>(null);

  const catId = searchParams.get('catId');

  useEffect(() => {
    // Get user's location
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setUserLocation([position.coords.latitude, position.coords.longitude]);
          setLocationLoading(false);
        },
        (error) => {
          console.log('Geolocation error:', error);
          setLocationLoading(false);
        },
        { timeout: 5000 }
      );
    } else {
      setLocationLoading(false);
    }
  }, []);

  useEffect(() => {
    const fetchCats = async () => {
      try {
        const data = await catService.getAll();
        setCats(data.filter(hasValidLocation));
      } catch (error) {
        console.error('Failed to fetch cats:', error);
      } finally {
        setCatsLoading(false);
      }
    };
    fetchCats();
  }, []);

  useEffect(() => {
    if (catId && cats.length > 0) {
      const targetCat = cats.find((c) => c._id === catId);
      if (targetCat) {
        setTargetCenter([targetCat.location.latitude, targetCat.location.longitude]);
      }
    }
  }, [catId, cats]);

  // Sydney University as fallback (where test cats are located)
  const defaultCenter: [number, number] = userLocation || [-33.8886, 151.1873];

  // Wait for both cats and location before rendering map
  if (catsLoading || locationLoading) {
    return (
      <Box
        sx={{
          height: 'calc(100vh - 64px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'linear-gradient(180deg, #0f0f1a 0%, #1a1a2e 100%)',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ height: 'calc(100vh - 64px)', position: 'relative' }}>
      <MapContainer
        center={defaultCenter}
        zoom={userLocation ? 16 : 14}
        style={{ height: '100%', width: '100%' }}
        ref={mapRef}
      >
        <TileLayer
          key={mapAppearance}
          className={mapAppearance === 'dark' ? 'catlas-map-tiles--dark' : ''}
          url="https://tile.openstreetmap.org/{z}/{x}/{y}.png"
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
        />

        <RecenterMap center={targetCenter} />

        {/* User location marker */}
        {userLocation && (
          <CircleMarker
            center={userLocation}
            radius={10}
            pathOptions={{
              fillColor: '#3b82f6',
              fillOpacity: 0.8,
              color: '#fff',
              weight: 3,
            }}
          >
            <Popup>
              <Typography variant="body2">Your location</Typography>
            </Popup>
          </CircleMarker>
        )}

        {/* Cat markers */}
        {cats.map((cat) => (
          <Marker
            key={cat._id}
            position={[cat.location.latitude, cat.location.longitude]}
            icon={catIcon}
          >
            <Popup>
              <Paper
                sx={{
                  p: 0,
                  minWidth: 220,
                  background: 'transparent',
                  boxShadow: 'none',
                }}
              >
                <Box
                  sx={{
                    width: '100%',
                    height: 140,
                    borderRadius: 2,
                    overflow: 'hidden',
                    mb: 1.5,
                  }}
                >
                  <img
                    src={getImageUrl(cat.imageUrl)}
                    alt={cat.name}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                    }}
                  />
                </Box>
                <Typography variant="h6" fontWeight="bold" sx={{ mb: 0.5 }}>
                  {cat.name}
                </Typography>
                {cat.breed && (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    {cat.breed}
                  </Typography>
                )}
                <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
                  {cat.likes_pats && (
                    <Chip
                      icon={<FavoriteBorder sx={{ fontSize: 16 }} />}
                      label="Likes Pats"
                      size="small"
                      color="success"
                      variant="outlined"
                    />
                  )}
                </Box>
                <Button
                  variant="contained"
                  size="small"
                  fullWidth
                  onClick={() => navigate(`/cats/${cat._id}`)}
                  sx={{ borderRadius: 2 }}
                >
                  View Profile
                </Button>
              </Paper>
            </Popup>
          </Marker>
        ))}
      </MapContainer>

      {/* Map appearance toggle (does not affect the application theme) */}
      <Paper
        elevation={6}
        sx={{
          position: 'absolute',
          top: 12,
          right: 12,
          zIndex: 1000,
          borderRadius: 2,
          overflow: 'hidden',
          background: 'rgba(15, 15, 26, 0.92)',
          backdropFilter: 'blur(10px)',
        }}
      >
        <ToggleButtonGroup
          exclusive
          size="small"
          value={mapAppearance}
          onChange={(_, value: 'light' | 'dark' | null) => {
            if (value) setMapAppearance(value);
          }}
          aria-label="Map appearance"
        >
          <ToggleButton value="light" aria-label="Light map" sx={{ gap: 0.75 }}>
            <LightMode fontSize="small" />
            Light
          </ToggleButton>
          <ToggleButton value="dark" aria-label="Dark map" sx={{ gap: 0.75 }}>
            <DarkMode fontSize="small" />
            Dark
          </ToggleButton>
        </ToggleButtonGroup>
      </Paper>

      {/* Cat count overlay */}
      <Box
        sx={{
          position: 'absolute',
          bottom: 24,
          left: 24,
          zIndex: 1000,
          background: 'rgba(26, 26, 46, 0.9)',
          backdropFilter: 'blur(10px)',
          borderRadius: 3,
          px: 2.5,
          py: 1.5,
          border: '1px solid rgba(255,255,255,0.1)',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PetsIcon sx={{ color: 'primary.light' }} />
          <Typography variant="body1" fontWeight="600">
            {cats.length} {cats.length === 1 ? 'cat' : 'cats'} on map
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default MapView;
